import { prettyId, type ValidationIssue } from '@/api/actions'

import { isObj } from './doc'

/**
 * Validation issues ↔ form fields. The server names a problem by its dotted path in the document
 * (`roles.thrower.types`, `program.2.tracks.ball.toss.to`); every form field carries its own path
 * in `data-field-path`, shows the issues at (or under) it, and the summary list scrolls to the
 * closest field that exists.
 */

export type Issue = ValidationIssue

const CLAUSE_TAGS = new Set(['role', 'relation', 'stage', 'state', 'any', 'not'])

/**
 * Make a server path match the document: pydantic puts a clause's kind (the union tag) in its
 * paths (`preconditions.0.relation.rel`, `…not.not.relation.b`) and a scalar union's branch after
 * the field (`program.0.dur.float`). Both are dropped by walking the document itself.
 */
export function normalizeIssuePath(path: string, doc: unknown): string {
  const parts = path.split('.').filter((p) => p !== '')
  const out: string[] = []
  let cur: unknown = doc
  for (let i = 0; i < parts.length; i++) {
    const p = parts[i]!
    const next = parts[i + 1]
    if (isObj(cur) && typeof cur.k === 'string' && cur.k === p && CLAUSE_TAGS.has(p) && (p === 'any' || p === 'not' ? next === p : next !== undefined)) continue
    if (cur !== undefined && cur !== null && typeof cur !== 'object') break
    out.push(p)
    if (Array.isArray(cur)) cur = /^\d+$/.test(p) ? cur[Number(p)] : undefined
    else if (isObj(cur)) cur = cur[p]
    else cur = undefined
  }
  return out.join('.')
}

export function normalizeIssues(issues: readonly Issue[], doc: unknown): Issue[] {
  const seen = new Set<string>()
  const out: Issue[] = []
  for (const i of issues) {
    const path = normalizeIssuePath(i.path, doc)
    const key = `${path}\u0000${i.message}`
    if (seen.has(key)) continue
    seen.add(key)
    out.push({ path, message: i.message })
  }
  return out
}

/** The issues at `path` (and, when `deep`, under it). */
export function issuesAt(issues: readonly Issue[], path: string, deep: boolean): Issue[] {
  return issues.filter((i) => i.path === path || (deep && (path === '' || i.path.startsWith(`${path}.`))))
}

/* -------------------------------------------------------------------------- */
/* Plain-words breadcrumbs                                                     */
/* -------------------------------------------------------------------------- */

const WORD: Record<string, string> = {
  id: 'Saved as',
  label: 'Name',
  description: 'Description',
  category: 'Kind of action',
  idea_template: 'How a story says it',
  enabled: 'Used in stories',
  duration: 'How long',
  min: 'shortest',
  default: 'usual',
  max: 'longest',
  types: 'what can play it',
  required: 'must be there',
  capabilities_all: 'must be able to',
  capabilities_any: 'at least one of',
  needs: 'body parts',
  kinds_any: 'kinds',
  plans_any: 'body shapes',
  state: 'must currently be',
  preconditions: 'What must be true first',
  clips: 'Moves',
  result: 'Afterwards',
  fallback: 'If it can’t happen',
  fx_auto: 'Automatic effects',
  when: 'When',
  event: 'what happens',
  subject: 'the thing',
  other: 'the other thing',
  priority: 'Priority',
  once_per_pair: 'once for each pair',
  supersedes: 'replaces',
  phase: 'phase',
  dur: 'how long',
  wait: 'pause after',
  clip: 'move',
  face: 'turns to',
  loops: 'times',
  layer: 'upper body',
  mask: 'body part mask',
  move: 'goes to',
  attach: 'is held by',
  detach: 'is let go',
  toss: 'flies',
  mount: 'climbs on',
  dismount: 'gets off',
  vanish: 'disappears',
  appear: 'appears',
  react: 'reacts',
  sync: 'times it',
  spawn: 'new things appear',
  to: 'to',
  from: 'from',
  at: 'at',
  at_s: 'after',
  at_event: 'when',
  stop: 'stops',
  socket: 'body part',
  apex: 'how high',
  then: 'then',
  with: 'with',
  kind: 'effect',
  fx: 'effect',
  rel: 'how',
  a: 'first',
  b: 'second',
  ratio: 'by',
  has: 'the place has',
  world_any: 'worlds',
  ref: 'what',
  eq: 'is',
  ne: 'is not',
  in: 'is one of',
  set: 'change',
  action: 'action',
  map: 'who plays who',
  noun: 'thing',
  count: 'how many',
  fall: 'falls',
  speed: 'speed',
}

/** `program.1.tracks.ball.toss.to` → "Step 2 › Ball › flies › to". */
export function describeIssuePath(path: string, kind: 'action' | 'rule' = 'action'): string {
  if (!path) return kind === 'rule' ? 'The world rule' : 'The action'
  const parts = path.split('.')
  const out: string[] = []
  for (let i = 0; i < parts.length; i++) {
    const p = parts[i]!
    const next = parts[i + 1]
    const num = next !== undefined && /^\d+$/.test(next) ? Number(next) + 1 : null
    if ((p === 'program' || (p === 'then' && i === 0)) && num) {
      out.push(`Step ${num}`)
      i++
    } else if (p === 'preconditions' && num) {
      out.push(WORD.preconditions!, `Check ${num}`)
      i++
    } else if (p === 'result' && num) {
      out.push(WORD.result!, `Change ${num}`)
      i++
    } else if (p === 'fallback' && num) {
      out.push(WORD.fallback!, `Option ${num}`)
      i++
    } else if (p === 'fx_auto' && num) {
      out.push(`Automatic effect ${num}`)
      i++
    } else if (p === 'fx' && num) {
      out.push(`Effect ${num}`)
      i++
    } else if (p === 'any' && num) {
      out.push(`one of them, ${num}`)
      i++
    } else if ((p === 'roles' || p === 'tracks') && next !== undefined) {
      if (p === 'roles') out.push('Who takes part')
      out.push(prettyId(next))
      i++
    } else if (p === 'clips' && next !== undefined && i === 0) {
      out.push(WORD.clips!, prettyId(next))
      i++
    } else if (/^\d+$/.test(p)) {
      out.push(`#${Number(p) + 1}`)
    } else {
      out.push(WORD[p] ?? p.replace(/_/g, ' '))
    }
  }
  const text = out.join(' › ')
  return text.charAt(0).toUpperCase() + text.slice(1)
}

/* -------------------------------------------------------------------------- */
/* Scrolling                                                                   */
/* -------------------------------------------------------------------------- */

const FOCUSABLE = 'input:not([type=hidden]), select, textarea, button'

/**
 * Scroll to the field for `path` — or, when it is not on screen, its closest parent — and focus
 * its first control. Returns the path that was found (`null` when nothing matched).
 */
export function scrollToPath(root: ParentNode, path: string): string | null {
  const parts = path.split('.').filter(Boolean)
  for (let n = parts.length; n >= 0; n--) {
    const p = parts.slice(0, n).join('.')
    const el = root.querySelector<HTMLElement>(`[data-field-path="${p}"]`)
    if (!el) continue
    el.scrollIntoView?.({ block: 'center', behavior: 'smooth' })
    const target = el.matches(FOCUSABLE) ? el : el.querySelector<HTMLElement>(FOCUSABLE)
    target?.focus({ preventScroll: true })
    return p
  }
  return null
}
